import { MeetingContent } from "data/@types/GlobalSearchData";
import { globalSearchList } from "data/services/api/globalSearch";
import { createMeetingFilters } from "data/services/api/meeting/meetingFilters";
import { useCAOfficeLocationsQuery } from "data/services/api/meeting/query";
import { useAdvancedViewFiltersStore } from "data/store/global-search/AdvancedViewFiltersStore";
import { useGlobalSearchStore } from "data/store/global-search/GlobalSearch";
import { useCallback, useEffect, useMemo, useState } from "react";
import { transformMeetingRow } from "utils/global-search/meetingTransformers";
import { useStableFilterCriteria } from "utils/global-search/useStableFilterCriteria";

export type { MeetingTableRow } from "data/@types/GlobalSearchMeetingTable";

export const useGlobalSearchMeetingsTable = (initialRows: MeetingContent[]) => {
  const { activeFilters, setMeetingFilterCriteria } =
    useAdvancedViewFiltersStore((state) => ({
      activeFilters: state.filtersByActivity["Meetings"] ?? {},
      setMeetingFilterCriteria: state.setMeetingFilterCriteria,
    }));
  const searchTerm = useGlobalSearchStore((state) => state.searchTerm);

  const { data: caOfficeLocations = [] } = useCAOfficeLocationsQuery();

  const [rows, setRows] = useState<MeetingContent[]>(initialRows);
  const [isBackendLoading, setIsBackendLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  const meetingFilters = useMemo(
    () => createMeetingFilters(caOfficeLocations),
    [caOfficeLocations],
  );

  const backendFilterCriteria = useStableFilterCriteria(
    meetingFilters,
    activeFilters,
  );

  // Publish criteria to store so useGlobalSearch can combine phrase + filters
  useEffect(() => {
    setMeetingFilterCriteria(backendFilterCriteria);
    return () => setMeetingFilterCriteria(undefined);
  }, [backendFilterCriteria, setMeetingFilterCriteria]);

  // When a phrase is active, use phrase results from parent (phrase query already applied filters)
  useEffect(() => {
    if (searchTerm) {
      setRows(initialRows);
    }
  }, [initialRows, searchTerm]);

  // Fetch from backend when no phrase is active (initial load, phrase cleared, or filters changed)
  useEffect(() => {
    if (searchTerm) return;

    let cancelled = false;

    const debounce = setTimeout(() => {
      setIsBackendLoading(true);
      setFetchError(false);

      globalSearchList("MEETING", "desc", backendFilterCriteria)
        .then(({ data }) => {
          if (!cancelled) setRows(data?.meeting?.content ?? []);
        })
        .catch((err) => {
          console.error(err);
          if (!cancelled) {
            setRows([]);
            setFetchError(true);
          }
        })
        .finally(() => {
          if (!cancelled) setIsBackendLoading(false);
        });
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(debounce);
    };
  }, [backendFilterCriteria, searchTerm]);

  // All filtering is server-side; applyFilters only transforms rows for the DataGrid
  const applyFilters = useCallback(
    (meetingContent: MeetingContent[]) =>
      meetingContent.map(transformMeetingRow),
    [],
  );

  return {
    applyFilters,
    meetingFilters,
    rows,
    isBackendLoading,
    fetchError,
  };
};
